import { cn } from '@/lib/utils';
import { forwardRef, type CSSProperties, type SVGProps } from 'react';
import { ArtSvg } from './art-svg.component';
import { CANDY_ART } from './candy-art.data';
import type { IconDefinition } from './icon-definition';
import { useIconStyle } from './icon-style.context';
import { PlainIcon } from './plain-icon.component';

type IconSize =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'lg'
  | 'xl'
  | '2xl'
  | '1x'
  | '2x'
  | '3x'
  | '4x'
  | '5x';

export interface AppIconProps extends Omit<
  SVGProps<SVGSVGElement>,
  'ref' | 'fill' | 'children'
> {
  icon: IconDefinition;
  /** Pick a specific candy drawing, for a place where the icon means
   * something narrower. Plain draws the icon's own Lucide icon. */
  candy?: string;
  /** Draw in the text colour in either style, for an icon whose colour is
   * its state (an unselected star beside a gold one). */
  mono?: boolean;
  spin?: boolean;
  /** Hold the 1.25em width even where a class would size the icon down. */
  fixedWidth?: boolean;
  size?: IconSize;
  title?: string;
  flip?: 'horizontal' | 'vertical' | 'both';
  rotation?: 90 | 180 | 270;
}

/**
 * The app's icon. It is 1em tall and 1.25em wide, like the Font Awesome icons
 * it replaced, so `className` sizes (`h-4 w-4`) and font sizes still apply.
 * The Candy style draws Cleanlist's drawing for the icon's name in colour; the
 * Plain style draws its Lucide icon in the current text colour.
 */
export const AppIcon = forwardRef<SVGSVGElement, AppIconProps>(function AppIcon(
  {
    icon,
    candy,
    mono,
    spin,
    fixedWidth,
    size,
    title,
    flip,
    rotation,
    className,
    style,
    ...rest
  },
  ref
) {
  const { style: iconStyle } = useIconStyle();
  const classes = cn(
    'cl-icon',
    size && `cl-icon-${size}`,
    spin && 'cl-icon-spin',
    fixedWidth && 'cl-icon-fw',
    flip && `cl-icon-flip-${flip}`,
    rotation && `cl-icon-rotate-${rotation}`,
    className
  );
  // As Font Awesome did: an icon with its own label or role is announced as
  // an image; any other icon is decoration and hidden from screen readers.
  // A title is a hover tooltip only.
  const labelled = Boolean(
    rest['aria-label'] || rest['aria-labelledby'] || rest.role
  );
  const a11y = labelled
    ? { role: rest.role ?? 'img' }
    : { 'aria-hidden': rest['aria-hidden'] ?? true };

  if (icon.logo && icon.tinted) {
    const mask = `url(${icon.logo})`;
    return (
      <span
        {...a11y}
        aria-label={rest['aria-label'] as string | undefined}
        title={title}
        className={cn(classes, 'cl-icon-tinted')}
        style={{
          ...(style as CSSProperties),
          maskImage: mask,
          WebkitMaskImage: mask,
        }}
        data-icon={icon.name}
      />
    );
  }

  if (icon.logo) {
    return (
      <img
        src={icon.logo}
        alt={title ?? (rest['aria-label'] as string | undefined) ?? ''}
        className={cn(classes, 'cl-icon-logo')}
        style={style as CSSProperties}
        data-icon={icon.name}
        aria-hidden={labelled ? undefined : true}
      />
    );
  }

  if (!icon.mark && (mono || iconStyle === 'plain')) {
    return (
      <PlainIcon
        ref={ref}
        {...a11y}
        {...rest}
        name={icon.name}
        solid={icon.solid}
        title={title}
        className={classes}
        style={style}
      />
    );
  }

  const shapes = CANDY_ART[candy ?? icon.name] ?? CANDY_ART[icon.name];
  if (!shapes) return null;
  return (
    <ArtSvg
      ref={ref}
      {...a11y}
      {...rest}
      shapes={shapes}
      name={icon.name}
      title={title}
      className={classes}
      style={style}
    />
  );
});
